import { apiService } from '.';

const BASE_URL = '/api/survey';

export const asyncGetSurveys = async (params?: {
    search?: string;
    limit?: number;
    page?: number;
    status?: boolean;
    code?: string;
    title?: string;
    statusFilter?: string;
    startDate?: string;
    endDate?: string;
}) => {
    try {
        const { items: data }: { items: { items: any[]; length: number } } =
            await apiService.get(BASE_URL, params);
        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetSurveyById = async (id: string, status?: boolean) => {
    try {
        const queryParams = status !== undefined ? { status } : undefined;
        const { items: data }: { items: any } = await apiService.get(
            `${BASE_URL}/${id}`,
            queryParams
        );
        return data;
    } catch (error) {
        return undefined;
    }
};

export const asyncGetSurvey = async (id: string) => {
    try {
        // Obtener la encuesta
        const survey = await asyncGetSurveyById(id, true);

        if (!survey) return null;

        // Obtener las preguntas con respuestas
        const questions = await asyncGetQuestionsWithAnswers(id);

        const result = {
            ...survey,
            questions: Array.isArray(questions) ? questions : [],
        };

        return result;
    } catch (error) {
        console.error('Error in asyncGetSurvey:', error);
        return null;
    }
};

export const asyncCreateSurvey = async (data: any) => {
    try {
        const { items: result }: { items: any } = await apiService.post(
            BASE_URL,
            data
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncUpdateSurvey = async (id: string, data: any) => {
    try {
        const { items: result }: { items: any } = await apiService.patch(
            `${BASE_URL}/${id}`,
            data
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncDeleteSurvey = async (id: string) => {
    try {
        await apiService.delete(`${BASE_URL}/${id}`);
    } catch (error) {
        throw error;
    }
};

export const asyncDuplicateSurvey = async (id: string) => {
    try {
        const { items: result }: { items: any } = await apiService.post(
            `${BASE_URL}/${id}/duplicate`,
            {}
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncCreateSurveyResponse = async (data: any) => {
    try {
        const { items: result }: { items: any } = await apiService.post(
            `${BASE_URL}/response`,
            data
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncGetSurveyResponse = async (id: string) => {
    try {
        const { items: data }: { items: any } = await apiService.get(
            `${BASE_URL}/response/${id}`
        );
        return data;
    } catch (error) {
        throw error;
    }
};

export const asyncUpdateSurveyResponse = async (id: string, data: any) => {
    try {
        const { items: result }: { items: any } = await apiService.patch(
            `${BASE_URL}/response/${id}`,
            data
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncGetIncompleteSurveyResponse = async (
    surveyId: string,
    identifier: string
) => {
    try {
        const { items: data }: { items: any } = await apiService.get(
            `${BASE_URL}/response/incomplete/${surveyId}/${identifier}`
        );
        return data;
    } catch (error) {
        return null;
    }
};

export const asyncGetSurveyHistorical = async (id: string) => {
    try {
        const { items: data }: { items: any[] } = await apiService.get(
            `${BASE_URL}/historical/${id}`
        );
        return data;
    } catch (error) {
        return [];
    }
};

export const asyncGetSurveyLogs = async (id: string) => {
    try {
        const { items: data }: { items: any[] } = await apiService.get(
            `${BASE_URL}/logs/${id}`
        );
        return data;
    } catch (error) {
        return [];
    }
};

export const asyncGetSurveyResponses = async (
    surveyId: string,
    params?: {
        limit?: number;
        page?: number;
        status?: string;
        documentNumber?: string;
        customerName?: string;
        associatedService?: string;
        startDate?: string;
        endDate?: string;
    }
) => {
    try {
        const { items: data }: { items: { items: any[]; length: number } } =
            await apiService.get(`${BASE_URL}/${surveyId}/responses`, params);
        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetAllSurveyResponses = async (params?: {
    limit?: number;
    page?: number;
    status?: string;
    surveyId?: string;
    surveyCode?: string;
    document_number?: string;
    customer_name?: string;
    associated_service?: string;
    startDate?: string;
    endDate?: string;
}) => {
    try {
        const { items: data }: { items: { items: any[]; length: number } } =
            await apiService.get(`${BASE_URL}/responses/all`, params);
        return data || { items: [], length: 0 };
    } catch (error) {
        return { items: [], length: 0 };
    }
};

export const asyncGetSurveyResponseLogs = async (responseId: string) => {
    try {
        const { items: data }: { items: any[] } = await apiService.get(
            `${BASE_URL}/response/${responseId}/logs`
        );
        return data;
    } catch (error) {
        return [];
    }
};

// Question services
export const asyncGetQuestions = async (surveyId: string) => {
    try {
        const { items: data }: { items: any[] } = await apiService.get(
            `/api/question/survey/${surveyId}`
        );
        return data;
    } catch (error) {
        return [];
    }
};

export const asyncGetQuestionsWithAnswers = async (surveyId: string) => {
    try {
        const { items: data }: { items: any[] } = await apiService.get(
            `/api/question/survey/${surveyId}/answers`
        );
        return data || [];
    } catch (error) {
        return [];
    }
};

export const asyncCreateQuestion = async (data: any) => {
    try {
        const { items: result }: { items: any } = await apiService.post(
            '/api/question',
            data
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncUpdateQuestion = async (id: string, data: any) => {
    try {
        const { items: result }: { items: any } = await apiService.patch(
            `/api/question/${id}`,
            data
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncDeleteQuestion = async (id: string) => {
    try {
        await apiService.delete(`/api/question/${id}`);
    } catch (error) {
        throw error;
    }
};

// Answer services
export const asyncGetAnswers = async (questionId: string) => {
    try {
        const { items: data }: { items: any[] } = await apiService.get(
            `/api/answer/question/${questionId}`
        );
        return data;
    } catch (error) {
        return [];
    }
};

export const asyncCreateAnswer = async (data: any) => {
    try {
        const { items: result }: { items: any } = await apiService.post(
            '/api/answer',
            data
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncUpdateAnswer = async (id: string, data: any) => {
    try {
        const { items: result }: { items: any } = await apiService.patch(
            `/api/answer/${id}`,
            data
        );
        return result;
    } catch (error) {
        throw error;
    }
};

export const asyncDeleteAnswer = async (id: string) => {
    try {
        await apiService.delete(`/api/answer/${id}`);
    } catch (error) {
        throw error;
    }
};
